import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, ChevronRight, ArrowRight, MapPin } from 'lucide-react';import { Button } from '../components/ui/button';
import { companyInfo } from '../data/mock';
import { getCityDataForService, getAllCitiesForService, getServiceMeta } from '../data/cityServiceData';
import SEOHead from '../components/SEOHead';
import CityServiceSchema from '../components/CityServiceSchema';
import { SubServicesList, TagsList, ServiceTestimonial, ServiceFAQs, SmallTagsList, OtherCityLinks } from '../components/CityServiceParts';
import { getBlogSlugForService, getBlogTitleForService } from '../components/InternalLinks';
import NearbyCities from '../components/NearbyCities';

const CityServicePage = () => {
  const { serviceId, city: citySlug } = useParams();
  const meta = getServiceMeta(serviceId);
  const city = getCityDataForService(serviceId, citySlug);

  if (!city || !meta) {
    return <Navigate to={`/services/${serviceId || ''}`} replace />;
  }

  const allCities = getAllCitiesForService(serviceId);
  const otherCities = allCities.filter(c => c.slug !== city.slug).slice(0, 6);
  const name = city.name;
  const t = city.testimonial;
  const faqs = city.faqs;
  const neighborhoods = city.neighborhoods;
  const zips = city.zip_codes;
  const sTitle = meta.title;
  const subServices = meta.sub_services;

  // Determine price labels - painting uses different field names
  const isPainting = serviceId === 'residential-painting';
  const priceLabel1 = isPainting ? 'Interior Painting' : city.price_label_1;
  const priceLabel2 = isPainting ? 'Exterior Painting' : city.price_label_2;
  const priceRange1 = isPainting ? city.price_range_interior : city.price_range_1;
  const priceRange2 = isPainting ? city.price_range_exterior : city.price_range_2;

  return (
    <div className="bg-white" data-testid="city-service-page">
      <SEOHead
        title={`${sTitle} ${name} TX | Free Estimates | Gray Contracting`}
        description={`Top-rated ${sTitle.toLowerCase()} in ${name}, TX. 55+ years serving DFW. Licensed, insured & certified. ${neighborhoods.slice(0, 3).join(', ')} & more. Free quotes: ${companyInfo.phone}`}
        keywords={`${meta.short.toLowerCase()} ${name} TX, ${sTitle.toLowerCase()} ${name}, ${meta.short.toLowerCase()} contractor ${name} Texas, best ${meta.short.toLowerCase()} near ${name}`}
        canonical={`/services/${serviceId}/${city.slug}`}
      />
      <CityServiceSchema city={city} serviceTitle={sTitle} serviceId={serviceId} />

      {/* Hero */}
      <section className="relative py-20 bg-neutral-900">
        <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{ backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/5a35076f-aa67-447c-9eac-34696923d44e/images/7ea633d787fac16cdf9482efc776fff53ba48ed0f841c6a96eca6dc12b513f75.png')" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-900/95 to-neutral-900/80" />
        <div className="relative z-10 max-w-6xl mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-neutral-400 mb-6" data-testid="city-service-breadcrumb">
            <Link to="/" className="hover:text-amber-400">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/services" className="hover:text-amber-400">Services</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/services/${serviceId}`} className="hover:text-amber-400">{meta.short}</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-amber-400">{name}, TX</span>
          </nav>
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-medium">{city.tagline}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight" data-testid="city-service-title">
            {sTitle}<br />in {name}, Texas
          </h1>
          <p className="text-lg text-neutral-300 max-w-2xl mb-8">
            Transform your {name} home with Gray Contracting's expert {sTitle.toLowerCase()} services.
            Over 55 years of trusted craftsmanship serving {name} and the Dallas-Fort Worth metroplex.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-semibold" data-testid="city-service-cta">
              <Link to="/contact">Get Free Estimate in {name} <ArrowRight className="w-5 h-5 ml-2" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <article className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-12 rounded-r-lg">
          <p className="text-sm text-neutral-700">
            <strong>Gray Contracting</strong> — 55+ years serving {name} and the Dallas-Fort Worth area.
            Family-owned, fully licensed & insured, lead & mold certified.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-neutral-900 mb-4">
                Why {name} Homeowners Choose Gray Contracting for {sTitle}
              </h2>
              <p className="text-neutral-700 leading-relaxed mb-4">{city.local_insight}</p>
              <p className="text-neutral-700 leading-relaxed">{city.climate_note}</p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">Our {sTitle} Services in {name}</h2>
              <SubServicesList items={subServices} />
            </section>

            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">{name} Neighborhoods We Serve</h2>
              <p className="text-neutral-700 mb-4">Our crews are familiar with every corner of {name}. We regularly work in these neighborhoods:</p>
              <TagsList items={neighborhoods} />
            </section>

            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-4">{sTitle} Costs in {name}, TX</h2>
              <p className="text-neutral-700 mb-6">Pricing varies based on project scope, materials, and complexity. Here are typical ranges for {name}:</p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="bg-neutral-50 rounded-xl p-6 border">
                  <h3 className="font-bold text-neutral-900 mb-2">{priceLabel1}</h3>
                  <div className="text-2xl font-bold text-amber-600 mb-2">{priceRange1}</div>
                  <p className="text-sm text-neutral-600">Average {name} home ({city.avg_home_size})</p>
                </div>
                <div className="bg-neutral-50 rounded-xl p-6 border">
                  <h3 className="font-bold text-neutral-900 mb-2">{priceLabel2}</h3>
                  <div className="text-2xl font-bold text-amber-600 mb-2">{priceRange2}</div>
                  <p className="text-sm text-neutral-600">Average {name} home ({city.avg_home_size})</p>
                </div>
              </div>
              <p className="text-sm text-neutral-500 mt-4">*Prices are estimates. Contact us for a free, detailed quote for your {name} property.</p>
            </section>

            <ServiceTestimonial text={t.text} author={t.author} neighborhood={t.neighborhood} cityName={name} />

            {/* Link to Blog Guide */}
            {getBlogSlugForService(serviceId) && (
              <section className="bg-neutral-50 border border-neutral-200 rounded-xl p-6">
                <h3 className="font-bold text-neutral-900 mb-2">Read Our Expert Guide</h3>
                <p className="text-neutral-600 text-sm mb-3">Learn everything about {sTitle.toLowerCase()} in the DFW area from our comprehensive guide:</p>
                <Link to={`/blog/${getBlogSlugForService(serviceId)}`} className="text-amber-600 hover:text-amber-700 font-medium inline-flex items-center gap-1">
                  {getBlogTitleForService(serviceId)} <ArrowRight className="w-4 h-4" />
                </Link>
              </section>
            )}

            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6">Frequently Asked Questions About {sTitle} in {name}</h2>
              <ServiceFAQs faqs={faqs} />
            </section>

            <NearbyCities citySlug={city.slug} serviceId={serviceId} serviceShort={meta.short} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-amber-500 rounded-xl p-6 text-white sticky top-24">
              <h3 className="text-xl font-bold mb-3">Free {meta.short} Estimate</h3>
              <p className="text-white/90 text-sm mb-4">Get a detailed, no-obligation quote for your {name} {meta.short.toLowerCase()} project.</p>
              <Button asChild className="w-full bg-neutral-900 hover:bg-neutral-800 text-white mb-3">
                <Link to="/contact">Request Estimate</Link>
              </Button>
              <a href={`tel:${companyInfo.phone}`} className="flex items-center justify-center gap-2 text-white font-semibold hover:text-white/80">
                <Phone className="w-4 h-4" />{companyInfo.phone}
              </a>
            </div>

            <div className="bg-neutral-50 rounded-xl p-6 border">
              <h3 className="font-bold text-neutral-900 mb-3">{name} Zip Codes We Serve</h3>
              <SmallTagsList items={zips} />
            </div>

            <div className="bg-neutral-50 rounded-xl p-6 border">
              <h3 className="font-bold text-neutral-900 mb-3">{meta.short} Also In</h3>
              <OtherCityLinks cities={otherCities} serviceId={serviceId} />
              <Link to={`/services/${serviceId}`} className="text-neutral-500 hover:text-amber-600 text-xs mt-3 block">
                View all {meta.short.toLowerCase()} services →
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Bottom CTA */}
      <section className="bg-neutral-900 py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready for {sTitle} in {name}?</h2>
          <p className="text-neutral-400 mb-6">55+ years of trusted service. Free estimates for all {name} homeowners.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600">
              <Link to="/contact">Get Free Estimate</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              <a href={`tel:${companyInfo.phone}`}><Phone className="w-5 h-5 mr-2" />{companyInfo.phone}</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityServicePage;
